/**
 * A booking session's idempotency key, sent with createWorkOrder. The server
 * answers the same key with the work order it already created, so a
 * double-click, a retry after a lost response or a resubmit after a reload
 * can never make a second booking (QA 2026-09-25 #1). Kept in the booking
 * slice (sessionStorage, this tab only) until the booking is made.
 *
 * Shape: `bk_` + a UUID, which the server's /^[A-Za-z0-9_-]{8,64}$/ accepts.
 */
/**
 * True when the server refused the mutation because it does not know one
 * of `fields` yet (a deploy where this client lands first): the caller asks
 * again without them.
 */
export function fieldsRefused(error: unknown, fields: string[]): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as {
    message?: string;
    graphQLErrors?: { message?: string }[];
    networkError?: { result?: { errors?: { message?: string }[] } } | null;
  };
  const messages = [
    e.message,
    ...(e.graphQLErrors || []).map((g) => g?.message),
    ...((e.networkError?.result?.errors) || []).map((g) => g?.message),
  ].map((m) => String(m || ''));
  const named = (m: string) => fields.some(
    (f) => new RegExp(`\\b${f}\\b`).test(m),
  );
  return messages.some((m) => named(m)
    && /not defined|Unknown argument|unknown field|Unknown type/i.test(m));
}

/**
 * True when the server refused the mutation only because it does not know
 * `idempotencyKey` yet (`Field "idempotencyKey" is not defined by type
 * "CreateWorkOrderInput"`), as a GraphQL error or inside a 400 response.
 */
export function idempotencyKeyRefused(error: unknown): boolean {
  return fieldsRefused(error, ['idempotencyKey']);
}

export function newBookingKey(): string {
  const cryptoApi = typeof window !== 'undefined'
    ? (window.crypto as Crypto & { randomUUID?: () => string })
    : undefined;
  if (cryptoApi?.randomUUID) return `bk_${cryptoApi.randomUUID()}`;
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return `bk_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `bk_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}
